import { hasAuthKey } from 'utils/api/auth-utils';

import {
    FULL_ACCOUNT_DEFAULT_PATTERN,
    GOALIE_ACCOUNT_DEFAULT_PATTERNS,
    SHIFTS_ACCOUNT_DEFAULT_PATTERNS
} from './launcher-config';

/** Paths of the links pinned to the header, in their order */
export type HeaderPins = string[];

export type HeaderPinsUserId = number | null | undefined;

export interface HeaderPinLink {
    to: string;
    submenu?: HeaderPinLink[];
    [field: string]: unknown;
}

export interface HeaderPinsRole {
    shiftsOnly?: boolean;
    goalieOnly?: boolean;
}

type StorageKey = string | null;

interface HeaderPinsChange {
    key: StorageKey;
    paths: HeaderPins | null;
}

// Pins are stored per user: one browser can be shared, and an admin can open a client's session in
// another tab (sessionStorage auth, shared localStorage) - one global key mixed their pins.
const STORAGE_KEY_PREFIX = 'tpe.headerNav.pins';
// Global key used before pins were scoped per user; adopted once by the first user who reads it.
const LEGACY_STORAGE_KEY = STORAGE_KEY_PREFIX;
const CHANGE_EVENT = 'tpe-header-pins-changed';

const storageKeyFor = (userId: HeaderPinsUserId): StorageKey =>
    userId == null ? null : `${STORAGE_KEY_PREFIX}.${userId}`;
// Set while the pins in local storage are changes the backend has not got (it could not be reached)
const pendingKeyFor = (userId: number) => `${STORAGE_KEY_PREFIX}.${userId}.pending`;

// Raw value -> parsed pins per key: keeps the returned array referentially stable between reads.
const parsedCache = new Map<string, { raw: string | null; pins: HeaderPins | null }>();
// Pins that could not be written (storage unavailable, quota exceeded, no user yet) and the pins synced with the
// backend. They win over storage, so components mounted later see the same pins as the rest of the UI.
const memoryPins = new Map<StorageKey, HeaderPins>();

const parsePins = (raw: string | null): HeaderPins | null => {
    if (!raw) return null;

    try {
        const parsed: unknown = JSON.parse(raw);

        return Array.isArray(parsed) && parsed.every(item => typeof item === 'string') ? parsed : null;
    } catch {
        return null;
    }
};

const readRaw = (key: string): string | null => {
    const raw = localStorage.getItem(key);
    if (raw !== null || key === LEGACY_STORAGE_KEY) {
        return raw;
    }

    const legacyRaw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacyRaw === null) {
        return null;
    }

    localStorage.setItem(key, legacyRaw);
    localStorage.removeItem(LEGACY_STORAGE_KEY);

    return legacyRaw;
};

const notifyHeaderPins = (change: HeaderPinsChange) => {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(
            new CustomEvent<HeaderPinsChange>(CHANGE_EVENT, { detail: change })
        );
    }
};

export const getStoredHeaderPins = (userId: HeaderPinsUserId): HeaderPins | null => {
    const key = storageKeyFor(userId);
    if (!key) return memoryPins.get(null) ?? null;
    if (memoryPins.has(key)) return memoryPins.get(key) ?? null;

    let raw: string | null;
    try {
        raw = readRaw(key);
    } catch {
        return null;
    }

    const cached = parsedCache.get(key);
    if (cached && cached.raw === raw) {
        return cached.pins;
    }

    const pins = parsePins(raw);
    parsedCache.set(key, { raw, pins });

    return pins;
};

export const setStoredHeaderPins = (paths: HeaderPins, userId: HeaderPinsUserId) => {
    if (!Array.isArray(paths)) {
        return;
    }

    const key = storageKeyFor(userId);
    const serialized = JSON.stringify(paths);
    if (JSON.stringify(getStoredHeaderPins(userId)) === serialized) {
        return;
    }

    try {
        if (!key) throw new Error('No user to store header pins for');
        localStorage.setItem(key, serialized);
        memoryPins.delete(key);
        parsedCache.set(key, { raw: serialized, pins: paths });
    } catch {
        // Storage unavailable or full: keep the pins for this page session
        memoryPins.set(key, paths);
    }

    notifyHeaderPins({ key, paths });
};

/** A signed-in user keeps the pins on the backend (LauncherItemsSync is mounted for them) */
export const canSyncHeaderPins = (userId: HeaderPinsUserId): userId is number => userId != null && hasAuthKey();

// Users whose pins are synced with the backend right now: their items are loaded and the sync is mounted.
// Until then (and after a failed request) the pins stay in local storage, so nothing is lost when the backend
// cannot be reached.
const syncedUsers = new Set<number>();

export const setHeaderPinsSynced = (userId: number, synced: boolean) => {
    if (synced) {
        syncedUsers.add(userId);
    } else {
        syncedUsers.delete(userId);
    }
};

/** The pins of a user synced with the backend: kept in memory for the page session, they win over local storage */
export const setSyncedHeaderPins = (paths: HeaderPins, userId: number) => {
    const key = storageKeyFor(userId);
    memoryPins.set(key, paths);
    notifyHeaderPins({ key, paths });
};

/** The pins in local storage, not the ones in memory: what this browser kept before the backend did or without it */
export const getLocalHeaderPins = (userId: number): HeaderPins | null => {
    const key = storageKeyFor(userId);
    try {
        return key ? parsePins(readRaw(key)) : null;
    } catch {
        return null;
    }
};

/** Whether the pins in local storage were changed while the backend could not be reached */
export const hasPendingHeaderPins = (userId: number) => {
    try {
        return localStorage.getItem(pendingKeyFor(userId)) !== null;
    } catch {
        return false;
    }
};

/** Local storage held the pins before the backend did (or while it could not be reached): cleared once it has them */
export const clearLocalHeaderPins = (userId: number) => {
    const key = storageKeyFor(userId);
    try {
        if (key && localStorage.getItem(key) !== null) localStorage.removeItem(key);
        localStorage.removeItem(pendingKeyFor(userId));
    } catch (error) {
        // eslint-disable-next-line no-console -- nothing to show: the pins work from the backend and memory
        console.warn('Header pins: local storage is unavailable:', error);
    }
};

/** Keeps pins the backend has not got in local storage, marked as pending: they win over the backend's on the next load */
export const savePendingHeaderPins = (paths: HeaderPins, userId: number) => {
    const key = storageKeyFor(userId);
    try {
        if (key) localStorage.setItem(key, JSON.stringify(paths));
        localStorage.setItem(pendingKeyFor(userId), '1');
    } catch (error) {
        // eslint-disable-next-line no-console -- the change stays in memory for this page session
        console.warn('Header pins: local storage is unavailable:', error);
    }
};

/**
 * Saves the pins chosen by the user: to the backend while the sync runs, to local storage otherwise. For a signed-in
 * user whose backend cannot be reached, the change is marked as pending: the sync sends it once the backend answers.
 */
export const persistHeaderPins = (paths: HeaderPins, userId: HeaderPinsUserId) => {
    if (userId != null && syncedUsers.has(userId) && hasAuthKey()) {
        setSyncedHeaderPins(paths, userId);

        return;
    }

    setStoredHeaderPins(paths, userId);
    if (canSyncHeaderPins(userId)) {
        try {
            localStorage.setItem(pendingKeyFor(userId), '1');
        } catch (error) {
            // eslint-disable-next-line no-console -- the change stays in memory for this page session
            console.warn('Header pins: local storage is unavailable:', error);
        }
    }
};

export const subscribeHeaderPins = (listener: (paths: HeaderPins | null) => void, userId: HeaderPinsUserId) => {
    if (typeof window === 'undefined') {
        return () => {};
    }

    const key = storageKeyFor(userId);

    const handler = (event: Event) => {
        if (event.type === CHANGE_EVENT) {
            const change = (event as CustomEvent<HeaderPinsChange>).detail;
            if (change?.key === key) listener(change.paths);

            return;
        }

        // Another tab: only this user's key, or a full clear (key === null)
        const storageKey = (event as StorageEvent).key;
        if (storageKey === key || storageKey === null) {
            listener(getStoredHeaderPins(userId));
        }
    };

    window.addEventListener(CHANGE_EVENT, handler);
    window.addEventListener('storage', handler);

    return () => {
        window.removeEventListener(CHANGE_EVENT, handler);
        window.removeEventListener('storage', handler);
    };
};

const matchDefaultPatterns = (links: HeaderPinLink[], patterns: RegExp[]) =>
    links.filter(link => patterns.some(pattern => pattern.test(link.to))).map(link => link.to);

export const getDefaultHeaderPinPaths = (
    links: HeaderPinLink[] | null | undefined,
    { shiftsOnly, goalieOnly }: HeaderPinsRole = {}
): HeaderPins => {
    const safeLinks = links ?? [];

    if (shiftsOnly) {
        return matchDefaultPatterns(safeLinks, SHIFTS_ACCOUNT_DEFAULT_PATTERNS);
    }

    if (goalieOnly) {
        return matchDefaultPatterns(safeLinks, GOALIE_ACCOUNT_DEFAULT_PATTERNS);
    }

    return safeLinks.filter(link => FULL_ACCOUNT_DEFAULT_PATTERN.test(link.to)).map(link => link.to);
};

interface BuildNextHeaderPinsParams {
    storedPins: HeaderPins | null | undefined;
    defaultPins: HeaderPins | null | undefined;
    visibleLinks: HeaderPinLink[] | null | undefined;
    path: string;
    checked: boolean;
}

/**
 * Toggle a pin while preserving stored pins for links that are not currently visible.
 */
export const buildNextHeaderPins = ({
    storedPins,
    defaultPins,
    visibleLinks,
    path,
    checked
}: BuildNextHeaderPinsParams): HeaderPins => {
    const visibleSet = new Set((visibleLinks ?? []).map(link => link.to));
    const stored = storedPins ?? [];
    // Stored pins may belong to another product; if none apply here, start from the defaults.
    const visibleStored = stored.filter(pin => visibleSet.has(pin));
    const base = visibleStored.length ? visibleStored : defaultPins ?? [];

    // Keep pins for links that are not in the current visible customize list.
    const hiddenPins = stored.filter(pin => !visibleSet.has(pin) && pin !== path);
    const visibleBase = base.filter(pin => visibleSet.has(pin) && pin !== path);
    const nextVisible = checked ? [...visibleBase, path] : visibleBase;

    return [...hiddenPins, ...nextVisible];
};
